import { ref, onMounted, onBeforeUnmount, defineComponent, h, watchEffect, unref, reactive, computed, toRaw } from 'vue';
import hypenateStyleName from 'hyphenate-style-name';
import { render, renderNodeRule, defaultMetaTransformer } from 'datocms-structured-text-generic-html-renderer';
export { renderMarkRule, renderNodeRule, renderNodeRule as renderRule } from 'datocms-structured-text-generic-html-renderer';
import { isRoot, isInlineItem, RenderError, isStructuredText, isItemLink, isBlock, isInlineBlock } from 'datocms-structured-text-utils';
export { RenderError } from 'datocms-structured-text-utils';
import { subscribeToQuery } from 'datocms-listen';

const isSsr = () => {
  return typeof window === "undefined";
};
const isIntersectionObserverAvailable = () => {
  return isSsr() ? false : !!window.IntersectionObserver;
};
const universalBtoa = (str) => isSsr() ? Buffer.from(str.toString(), "binary").toString("base64") : window.btoa(str);
const absolutePositioning = {
  position: "absolute",
  left: "0px",
  top: "0px",
  width: "100%",
  height: "100%"
};
const escapeString = (s) => {
  let result = `${s}`;
  result = result.replace(/&/g, "&amp;");
  result = result.replace(/</g, "&lt;");
  result = result.replace(/>/g, "&gt;");
  result = result.replace(/"/g, "&quot;");
  result = result.replace(/'/g, "&#39;");
  return result;
};
const toCss = (object) => {
  if (!object) {
    return null;
  }
  let result = "";
  for (const styleName in object) {
    if (Object.prototype.hasOwnProperty.call(object, styleName) && object[styleName]) {
      result += `${hypenateStyleName(styleName)}: ${object[styleName]}; `;
    }
  }
  return result.length > 0 ? result : null;
};
const tag = (tagName, attrs, content) => {
  const serializedAttrs = [];
  if (attrs) {
    for (const attrName in attrs) {
      if (Object.prototype.hasOwnProperty.call(attrs, attrName)) {
        const value = attrs[attrName];
        if (value) {
          serializedAttrs.push(
            `${escapeString(attrName)}="${escapeString(value)}"`
          );
        }
      }
    }
  }
  const attrsString = serializedAttrs.length > 0 ? ` ${serializedAttrs.join(" ")}` : "";
  return content ? `<${tagName}${attrsString}>${content.join("")}</${tagName}>` : `<${tagName}${attrsString} />`;
};

const useInView = ({
  threshold,
  rootMargin
}) => {
  const observer = ref(null);
  const elRef = ref(null);
  const inView = ref(false);
  onMounted(() => {
    if (isIntersectionObserverAvailable()) {
      observer.value = new IntersectionObserver(
        (entries) => {
          if (entries.some(({ isIntersecting }) => isIntersecting) && observer.value) {
            inView.value = true;
            observer.value.disconnect();
          }
        },
        {
          threshold,
          rootMargin
        }
      );
      if (elRef.value) {
        observer.value.observe(elRef.value);
      }
    }
  });
  onBeforeUnmount(() => {
    if (isIntersectionObserverAvailable() && observer.value) {
      observer.value.disconnect();
    }
  });
  return { inView, elRef };
};

const Source = defineComponent({
  props: {
    srcset: {
      type: String
    },
    sizes: {
      type: String
    },
    type: {
      type: String
    }
  },
  setup({ srcset, sizes, type }) {
    return () => h("source", {
      srcset,
      sizes,
      type
    });
  }
});

const bogusBaseUrl = "https://example.com/";
const buildSrcSet = (src, width, candidateMultipliers) => {
  if (!src || !width) {
    return void 0;
  }
  return candidateMultipliers.map((multiplier) => {
    const url = new URL(src, bogusBaseUrl);
    if (multiplier !== 1) {
      url.searchParams.set("dpr", `${multiplier}`);
      const maxH = url.searchParams.get("max-h");
      const maxW = url.searchParams.get("max-w");
      if (maxH) {
        url.searchParams.set(
          "max-h",
          `${Math.floor(Number.parseInt(maxH) * multiplier)}`
        );
      }
      if (maxW) {
        url.searchParams.set(
          "max-w",
          `${Math.floor(Number.parseInt(maxW) * multiplier)}`
        );
      }
    }
    const finalWidth = Math.floor(width * multiplier);
    if (finalWidth < 50) {
      return null;
    }
    return `${url.toString().replace(bogusBaseUrl, "/")} ${finalWidth}w`;
  }).filter(Boolean).join(",");
};
function buildWebpSource(data, sizes) {
  var _a;
  return data.webpSrcSet && h(Source, {
    srcset: data.webpSrcSet,
    sizes: (_a = sizes != null ? sizes : data.sizes) != null ? _a : void 0,
    type: "image/webp"
  });
}
function buildRegularSource(data, sizes, srcSetCandidates) {
  var _a, _b;
  return h(Source, {
    srcset: (_a = data.srcSet) != null ? _a : buildSrcSet(data.src, data.width, srcSetCandidates),
    sizes: (_b = sizes != null ? sizes : data.sizes) != null ? _b : void 0
  });
}

var __defProp$6 = Object.defineProperty;
var __getOwnPropSymbols$6 = Object.getOwnPropertySymbols;
var __hasOwnProp$6 = Object.prototype.hasOwnProperty;
var __propIsEnum$6 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$6 = (obj, key, value) => key in obj ? __defProp$6(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$6 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$6.call(b, prop))
      __defNormalProp$6(a, prop, b[prop]);
  if (__getOwnPropSymbols$6)
    for (var prop of __getOwnPropSymbols$6(b)) {
      if (__propIsEnum$6.call(b, prop))
        __defNormalProp$6(a, prop, b[prop]);
    }
  return a;
};
const Sizer = defineComponent({
  props: {
    sizerClass: {
      type: String
    },
    sizerStyle: {
      type: Object,
      default: () => ({})
    },
    width: {
      type: Number
    },
    height: {
      type: Number
    }
  },
  setup({ sizerClass, sizerStyle, width, height }) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"></svg>`;
    return () => h("img", {
      class: sizerClass,
      style: __spreadValues$6({
        display: "block",
        width: "100%"
      }, sizerStyle),
      src: `data:image/svg+xml;base64,${universalBtoa(svg)}`,
      "aria-hidden": "true"
    });
  }
});

var __defProp$5 = Object.defineProperty;
var __defProps$3 = Object.defineProperties;
var __getOwnPropDescs$3 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$5 = Object.getOwnPropertySymbols;
var __hasOwnProp$5 = Object.prototype.hasOwnProperty;
var __propIsEnum$5 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$5 = (obj, key, value) => key in obj ? __defProp$5(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$5 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$5.call(b, prop))
      __defNormalProp$5(a, prop, b[prop]);
  if (__getOwnPropSymbols$5)
    for (var prop of __getOwnPropSymbols$5(b)) {
      if (__propIsEnum$5.call(b, prop))
        __defNormalProp$5(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$3 = (a, b) => __defProps$3(a, __getOwnPropDescs$3(b));
const imageAddStrategy = ({ priority, inView, loaded }) => {
  if (priority) {
    return true;
  }
  if (isSsr()) {
    return false;
  }
  if (isIntersectionObserverAvailable()) {
    return inView || loaded;
  }
  return true;
};
const imageShowStrategy = ({ priority, loaded }) => {
  if (priority) {
    return true;
  }
  if (isSsr()) {
    return false;
  }
  if (isIntersectionObserverAvailable()) {
    return loaded;
  }
  return true;
};
const Image$1 = defineComponent({
  name: "DatocmsImage",
  inheritAttrs: false,
  emits: ["load"],
  props: {
    data: {
      type: Object,
      required: true
    },
    pictureClass: {
      type: String
    },
    imgClass: {
      type: String
    },
    placeholderClass: {
      type: String
    },
    fadeInDuration: {
      type: Number
    },
    intersectionTreshold: {
      type: Number,
      default: 0
    },
    intersectionThreshold: {
      type: Number
    },
    intersectionMargin: {
      type: String,
      default: "0px 0px 0px 0px"
    },
    pictureStyle: {
      type: Object,
      default: () => ({})
    },
    imgStyle: {
      type: Object,
      default: () => ({})
    },
    placeholderStyle: {
      type: Object,
      default: () => ({})
    },
    layout: {
      type: String,
      default: () => "intrinsic",
      validator: (value) => ["intrinsic", "fixed", "responsive", "fill"].includes(value)
    },
    objectFit: {
      type: String
    },
    objectPosition: {
      type: String
    },
    usePlaceholder: {
      type: Boolean,
      default: true
    },
    sizes: {
      type: String
    },
    priority: {
      type: Boolean,
      default: false
    },
    srcSetCandidates: {
      type: Array,
      validator: (values) => values.every((value) => {
        return typeof value === "number";
      }),
      default: () => [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4]
    },
    referrerPolicy: {
      type: String,
      default: "no-referrer-when-downgrade"
    }
  },
  setup(props, { emit, expose }) {
    const loaded = ref(false);
    function handleLoad() {
      emit("load");
      loaded.value = true;
    }
    const { inView, elRef: rootRef } = useInView({
      threshold: props.intersectionThreshold || props.intersectionTreshold || 0,
      rootMargin: props.intersectionMargin || "0px 0px 0px 0px"
    });
    const imageRef = ref();
    watchEffect(() => {
      if (!imageRef.value) {
        return;
      }
      if (imageRef.value.complete && imageRef.value.naturalWidth) {
        handleLoad();
      }
    });
    expose({
      rootRef,
      imageRef
    });
    return {
      inView,
      rootRef,
      loaded,
      handleLoad,
      imageRef
    };
  },
  render() {
    var _a, _b, _c, _d, _e, _f;
    const addImage = imageAddStrategy({
      priority: this.priority,
      inView: this.inView,
      loaded: this.loaded
    });
    const showImage = imageShowStrategy({
      priority: this.priority,
      inView: this.inView,
      loaded: this.loaded
    });
    const webpSource = buildWebpSource(this.data, this.sizes);
    const regularSource = buildRegularSource(
      this.data,
      this.sizes,
      this.srcSetCandidates
    );
    const transition = typeof this.fadeInDuration === "undefined" || this.fadeInDuration > 0 ? `opacity ${this.fadeInDuration || 500}ms` : void 0;
    const basePlaceholderStyle = __spreadValues$5({
      transition,
      opacity: showImage ? 0 : 1,
      position: "absolute",
      left: "-5%",
      top: "-5%",
      width: "110%",
      height: this.data.base64 ? "auto" : "110%",
      maxWidth: "none",
      maxHeight: "none"
    }, this.placeholderStyle);
    const placeholder = this.usePlaceholder && this.data.base64 ? h("img", {
      "aria-hidden": "true",
      src: this.data.base64,
      class: this.placeholderClass,
      style: __spreadValues$5({
        objectFit: this.objectFit,
        objectPosition: this.objectPosition
      }, basePlaceholderStyle)
    }) : this.usePlaceholder && this.data.bgColor ? h("div", {
      class: this.placeholderClass,
      style: __spreadValues$5({
        backgroundColor: this.data.bgColor
      }, basePlaceholderStyle)
    }) : null;
    const { width, aspectRatio } = this.data;
    const height = (_a = this.data.height) != null ? _a : aspectRatio ? width / aspectRatio : 0;
    const sizer = this.layout !== "fill" ? h(Sizer, {
      sizerClass: this.imgClass,
      sizerStyle: this.imgStyle,
      width,
      height
    }) : null;
    return h(
      "div",
      {
        class: this.$attrs.class,
        style: __spreadValues$5(__spreadValues$5({
          overflow: "hidden"
        }, this.layout === "fill" ? absolutePositioning : this.layout === "intrinsic" ? { position: "relative", width: "100%", maxWidth: `${width}px` } : this.layout === "fixed" ? { position: "relative", width: `${width}px` } : { position: "relative", width: "100%" }), this.$attrs.style || {}),
        ref: "rootRef"
      },
      [
        sizer,
        placeholder,
        addImage && h("picture", { class: this.pictureClass, style: this.pictureStyle }, [
          webpSource,
          regularSource,
          this.data.src && h("img", {
            src: this.data.src,
            alt: this.data.alt,
            title: this.data.title,
            fetchpriority: this.priority ? "high" : void 0,
            onLoad: this.handleLoad,
            ref: "imageRef",
            class: this.imgClass,
            style: __spreadValues$5(__spreadProps$3(__spreadValues$5({
              opacity: showImage ? 1 : 0,
              transition
            }, absolutePositioning), {
              objectFit: this.objectFit,
              objectPosition: this.objectPosition
            }), this.imgStyle),
            referrerpolicy: this.referrerPolicy
          })
        ]),
        h("noscript", {
          innerHTML: tag(
            "picture",
            { class: this.pictureClass, style: toCss(this.pictureStyle) },
            [
              this.data.webpSrcSet && tag("source", {
                srcset: this.data.webpSrcSet,
                sizes: (_c = (_b = this.sizes) != null ? _b : this.data.sizes) != null ? _c : void 0,
                type: "image/webp"
              }),
              tag("source", {
                srcset: (_d = this.data.srcSet) != null ? _d : buildSrcSet(
                  this.data.src,
                  this.data.width,
                  this.srcSetCandidates
                ),
                sizes: (_f = (_e = this.sizes) != null ? _e : this.data.sizes) != null ? _f : void 0
              }),
              tag("img", {
                src: this.data.src,
                alt: this.data.alt,
                title: this.data.title,
                class: this.imgClass,
                style: toCss(__spreadValues$5(__spreadProps$3(__spreadValues$5({}, absolutePositioning), {
                  objectFit: this.objectFit,
                  objectPosition: this.objectPosition
                }), this.imgStyle)),
                loading: this.priority ? void 0 : "lazy",
                fetchpriority: this.priority ? "high" : void 0,
                referrerpolicy: this.referrerPolicy
              })
            ]
          )
        })
      ]
    );
  }
});
const DatocmsImagePlugin = {
  install: (Vue) => {
    Vue.component("DatocmsImage", Image$1);
  }
};

var __defProp$4 = Object.defineProperty;
var __getOwnPropSymbols$4 = Object.getOwnPropertySymbols;
var __hasOwnProp$4 = Object.prototype.hasOwnProperty;
var __propIsEnum$4 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$4 = (obj, key, value) => key in obj ? __defProp$4(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$4 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$4.call(b, prop))
      __defNormalProp$4(a, prop, b[prop]);
  if (__getOwnPropSymbols$4)
    for (var prop of __getOwnPropSymbols$4(b)) {
      if (__propIsEnum$4.call(b, prop))
        __defNormalProp$4(a, prop, b[prop]);
    }
  return a;
};
const NakedImage = defineComponent({
  name: "DatocmsNakedImage",
  inheritAttrs: false,
  props: {
    data: {
      type: Object,
      required: true
    },
    usePlaceholder: {
      type: Boolean,
      default: true
    },
    sizes: {
      type: String
    },
    priority: {
      type: Boolean,
      default: false
    },
    srcSetCandidates: {
      type: Array,
      validator: (values) => values.every((value) => {
        return typeof value === "number";
      }),
      default: () => [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4]
    },
    pictureClass: {
      type: String
    },
    pictureStyle: {
      type: Object,
      default: () => ({})
    },
    imgClass: {
      type: String
    },
    imgStyle: {
      type: Object,
      default: () => ({})
    },
    referrerPolicy: {
      type: String,
      default: "no-referrer-when-downgrade"
    }
  },
  setup(_props, { emit, expose }) {
    const loaded = ref(false);
    function handleLoad() {
      emit("load");
      loaded.value = true;
    }
    const imageRef = ref();
    watchEffect(() => {
      if (!imageRef.value) {
        return;
      }
      if (imageRef.value.complete && imageRef.value.naturalWidth) {
        handleLoad();
      }
    });
    expose({
      imageRef
    });
    return {
      loaded,
      handleLoad,
      imageRef
    };
  },
  render() {
    var _a;
    const webpSource = buildWebpSource(this.data, this.sizes);
    const regularSource = buildRegularSource(
      this.data,
      this.sizes,
      this.srcSetCandidates
    );
    const { width } = this.data;
    const height = (_a = this.data.height) != null ? _a : Math.round(this.data.aspectRatio ? width / this.data.aspectRatio : 0);
    const sizingStyle = {
      aspectRatio: `${width} / ${height}`,
      width: "100%",
      maxWidth: `${width}px`,
      height: "auto"
    };
    const placeholderStyle = this.usePlaceholder && !this.loaded && this.data.base64 ? {
      backgroundImage: `url("${this.data.base64}")`,
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "50% 50%",
      color: "transparent"
    } : this.usePlaceholder && !this.loaded && this.data.bgColor ? {
      backgroundColor: this.data.bgColor,
      color: "transparent"
    } : void 0;
    return h(
      "picture",
      {
        style: this.pictureStyle,
        class: this.pictureClass
      },
      [
        webpSource,
        regularSource,
        this.data.src && h("img", {
          ref: "imageRef",
          src: this.data.src,
          alt: this.data.alt,
          onLoad: this.handleLoad,
          title: this.data.title,
          fetchpriority: this.priority ? "high" : void 0,
          loading: this.priority ? void 0 : "lazy",
          style: __spreadValues$4(__spreadValues$4(__spreadValues$4({}, placeholderStyle), sizingStyle), this.imgStyle || {}),
          class: this.imgClass,
          referrerpolicy: this.referrerPolicy
        })
      ]
    );
  }
});
const DatocmsNakedImagePlugin = {
  install: (Vue) => {
    Vue.component("DatocmsNakedImage", Image);
  }
};

const defaultAdapter = {
  renderNode: h,
  renderMark: h,
  renderFragment: (children, _key) => children,
  renderText: (text, _key) => text
};
function appendKeyToValidElement(element, key) {
  if (element !== null && typeof element !== "string" && element.key === null) {
    element.key = key;
  }
  return element;
}
const StructuredText = defineComponent({
  name: "DatocmsStructuredText",
  props: {
    data: {
      type: Object
    },
    customRules: {
      type: Array
    },
    customNodeRules: {
      type: Array
    },
    customMarkRules: {
      type: Array
    },
    renderInlineRecord: {
      type: Function
    },
    renderLinkToRecord: {
      type: Function
    },
    renderBlock: {
      type: Function
    },
    renderInlineBlock: {
      type: Function
    },
    metaTransformer: { type: Function },
    renderText: { type: Function },
    renderNode: { type: Function },
    renderFragment: { type: Function }
  },
  setup(props) {
    return () => {
      return render(props.data, {
        adapter: {
          renderText: props.renderText || defaultAdapter.renderText,
          renderNode: props.renderNode || defaultAdapter.renderNode,
          renderFragment: props.renderFragment || defaultAdapter.renderFragment
        },
        metaTransformer: props.metaTransformer,
        customMarkRules: props.customMarkRules,
        customNodeRules: [
          renderNodeRule(
            isRoot,
            ({ adapter: { renderNode }, key, children }) => {
              return renderNode("div", { key }, children);
            }
          ),
          renderNodeRule(isInlineItem, ({ node, key }) => {
            if (!props.renderInlineRecord) {
              throw new RenderError(
                `The Structured Text document contains an 'inlineItem' node, but no 'renderInlineRecord' prop is specified!`,
                node
              );
            }
            if (!isStructuredText(props.data) || !props.data.links) {
              throw new RenderError(
                `The Structured Text document contains an 'inlineItem' node, but .links is not present!`,
                node
              );
            }
            const item = props.data.links.find((item2) => item2.id === node.item);
            if (!item) {
              throw new RenderError(
                `The Structured Text document contains an 'inlineItem' node, but cannot find a record with ID ${node.item} inside .links!`,
                node
              );
            }
            return appendKeyToValidElement(
              props.renderInlineRecord({ record: item }),
              key
            );
          }),
          renderNodeRule(isItemLink, ({ node, key, children }) => {
            if (!props.renderLinkToRecord) {
              throw new RenderError(
                `The Structured Text document contains an 'itemLink' node, but no 'renderLinkToRecord' prop is specified!`,
                node
              );
            }
            if (!isStructuredText(props.data) || !props.data.links) {
              throw new RenderError(
                `The Structured Text document contains an 'itemLink' node, but .links is not present!`,
                node
              );
            }
            const item = props.data.links.find((item2) => item2.id === node.item);
            if (!item) {
              throw new RenderError(
                `The Structured Text document contains an 'itemLink' node, but cannot find a record with ID ${node.item} inside .links!`,
                node
              );
            }
            return appendKeyToValidElement(
              props.renderLinkToRecord({
                record: item,
                children,
                transformedMeta: node.meta ? (props.metaTransformer || defaultMetaTransformer)({
                  node,
                  meta: node.meta
                }) : null
              }),
              key
            );
          }),
          renderNodeRule(isBlock, ({ node, key }) => {
            if (!props.renderBlock) {
              throw new RenderError(
                `The Structured Text document contains a 'block' node, but no 'renderBlock' prop is specified!`,
                node
              );
            }
            if (!isStructuredText(props.data) || !props.data.blocks) {
              throw new RenderError(
                `The Structured Text document contains a 'block' node, but .blocks is not present!`,
                node
              );
            }
            const item = props.data.blocks.find(
              (item2) => item2.id === node.item
            );
            if (!item) {
              throw new RenderError(
                `The Structured Text document contains a 'block' node, but cannot find a record with ID ${node.item} inside .blocks!`,
                node
              );
            }
            return appendKeyToValidElement(
              props.renderBlock({ record: item }),
              key
            );
          }),
          renderNodeRule(isInlineBlock, ({ node, key }) => {
            if (!props.renderInlineBlock) {
              throw new RenderError(
                `The Structured Text document contains an 'inlineBlock' node, but no 'renderInlineBlock' prop is specified!`,
                node
              );
            }
            if (!isStructuredText(props.data) || !props.data.blocks) {
              throw new RenderError(
                `The Structured Text document contains an 'inlineBlock' node, but .blocks is not present!`,
                node
              );
            }
            const item = props.data.blocks.find(
              (item2) => item2.id === node.item
            );
            if (!item) {
              throw new RenderError(
                `The Structured Text document contains a 'block' node, but cannot find a record with ID ${node.item} inside .blocks!`,
                node
              );
            }
            return appendKeyToValidElement(
              props.renderInlineBlock({ record: item }),
              key
            );
          }),
          ...props.customNodeRules || props.customRules || []
        ]
      });
    };
  }
});
const DatocmsStructuredTextPlugin = {
  install: (Vue) => {
    Vue.component("DatocmsStructuredText", StructuredText);
  }
};

var __defProp$3 = Object.defineProperty;
var __getOwnPropSymbols$3 = Object.getOwnPropertySymbols;
var __hasOwnProp$3 = Object.prototype.hasOwnProperty;
var __propIsEnum$3 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$3 = (obj, key, value) => key in obj ? __defProp$3(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$3 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$3.call(b, prop))
      __defNormalProp$3(a, prop, b[prop]);
  if (__getOwnPropSymbols$3)
    for (var prop of __getOwnPropSymbols$3(b)) {
      if (__propIsEnum$3.call(b, prop))
        __defNormalProp$3(a, prop, b[prop]);
    }
  return a;
};
const computedTitle = (title) => {
  return title ? { title } : void 0;
};
const computedPlaybackId = (muxPlaybackId, playbackId) => {
  if (!(muxPlaybackId || playbackId))
    return void 0;
  return { playbackId: `${muxPlaybackId || playbackId}` };
};
const computedStyle = (width, height) => {
  if (!(width && height))
    return void 0;
  return {
    style: {
      aspectRatio: `${width} / ${height}`
    }
  };
};
const computedPlaceholder = (blurUpThumb) => {
  return blurUpThumb ? { placeholder: blurUpThumb } : void 0;
};
const useVideoPlayer = ({
  data
}) => {
  const { title, width, height, playbackId, muxPlaybackId, blurUpThumb } = data || {};
  if (data === void 0)
    return {};
  return __spreadValues$3(__spreadValues$3(__spreadValues$3(__spreadValues$3({}, computedTitle(title) || {}), computedPlaybackId(muxPlaybackId, playbackId) || {}), computedStyle(width, height) || {}), computedPlaceholder(blurUpThumb) || {});
};

var __defProp$2 = Object.defineProperty;
var __defProps$2 = Object.defineProperties;
var __getOwnPropDescs$2 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$2 = Object.getOwnPropertySymbols;
var __hasOwnProp$2 = Object.prototype.hasOwnProperty;
var __propIsEnum$2 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$2 = (obj, key, value) => key in obj ? __defProp$2(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$2 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$2.call(b, prop))
      __defNormalProp$2(a, prop, b[prop]);
  if (__getOwnPropSymbols$2)
    for (var prop of __getOwnPropSymbols$2(b)) {
      if (__propIsEnum$2.call(b, prop))
        __defNormalProp$2(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$2 = (a, b) => __defProps$2(a, __getOwnPropDescs$2(b));
var __objRest$1 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$2.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$2)
    for (var prop of __getOwnPropSymbols$2(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$2.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const isNil = (x) => x == void 0;
const isKeyOf = (k, o) => {
  if (isNil(o))
    return false;
  return k in o;
};
const PropToAttrNameMap = {
  crossOrigin: "crossorigin",
  viewBox: "viewBox",
  playsInline: "playsinline",
  autoPlay: "autoplay",
  playbackRate: "playbackrate",
  playbackRates: "playbackrates"
};
const toKebabCase = (string) => string.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
const toNativeAttrName = (propName, propValue) => {
  if (typeof propValue === "boolean" && !propValue)
    return void 0;
  if (isKeyOf(propName, PropToAttrNameMap))
    return PropToAttrNameMap[propName];
  if (typeof propValue == void 0)
    return void 0;
  if (/[A-Z]/.test(propName))
    return toKebabCase(propName);
  return propName;
};
const toNativeAttrValue = (propValue, propName) => {
  if (Array.isArray(propValue))
    return propValue.join(" ");
  if (typeof propValue === "boolean")
    return propValue;
  return propValue;
};
const toHTMLAttrs = (props = {}) => {
  return Object.entries(props).reduce(
    (transformedProps, [propName, propValue]) => {
      const attrName = toNativeAttrName(propName, propValue);
      if (!attrName) {
        return transformedProps;
      }
      const attrValue = toNativeAttrValue(propValue);
      if (attrValue === void 0) {
        return transformedProps;
      }
      transformedProps[attrName] = attrValue;
      return transformedProps;
    },
    {}
  );
};
const VideoPlayer = defineComponent({
  name: "DatocmsVideoPlayer",
  props: {
    _hlsConfig: {
      type: Object,
      required: false
    },
    accentColor: {
      type: String,
      required: false
    },
    audio: {
      type: Boolean,
      required: false
    },
    autoPlay: {
      type: [Boolean, String],
      required: false
    },
    backwardSeekOffset: {
      type: Number,
      required: false
    },
    beaconCollectionDomain: {
      type: String,
      required: false
    },
    crossOrigin: {
      type: String,
      required: false
    },
    currentTime: {
      type: Number,
      required: false
    },
    customDomain: {
      type: String,
      required: false
    },
    data: {
      type: Object,
      required: false
    },
    debug: {
      type: Boolean,
      required: false
    },
    defaultDuration: {
      type: Number,
      required: false
    },
    defaultHiddenCaptions: {
      type: Boolean,
      required: false
    },
    defaultShowRemainingTime: {
      type: Boolean,
      required: false
    },
    defaultStreamType: {
      type: String,
      required: false
    },
    disableCookies: {
      type: Boolean,
      required: false,
      default: () => true
    },
    disableTracking: {
      type: Boolean,
      required: false,
      default: () => true
    },
    disablePictureInPicture: {
      type: Boolean,
      required: false
    },
    envKey: {
      type: String,
      required: false
    },
    extraSourceParams: {
      type: Object,
      required: false
    },
    forwardSeekOffset: {
      type: Number,
      required: false
    },
    hotkeys: {
      type: String,
      required: false
    },
    loop: {
      type: Boolean,
      required: false
    },
    maxResolution: {
      type: String,
      required: false
    },
    metadata: {
      type: Object,
      required: false
    },
    metadataVideoId: {
      type: String,
      required: false
    },
    metadataVideoTitle: {
      type: String,
      required: false
    },
    metadataViewerUserId: {
      type: String,
      required: false
    },
    minResolution: {
      type: String,
      required: false
    },
    muted: {
      type: Boolean,
      required: false
    },
    nohotkeys: {
      type: String,
      required: false
    },
    noVolumePref: {
      type: Boolean,
      required: false
    },
    paused: {
      type: Boolean,
      required: false
    },
    placeholder: {
      type: String,
      required: false
    },
    playbackId: {
      type: String,
      required: false
    },
    playbackRate: {
      type: Number,
      required: false
    },
    playbackRates: {
      type: Array,
      required: false
    },
    playerSoftwareName: {
      type: String,
      required: false
    },
    playerSoftwareVersion: {
      type: String,
      required: false
    },
    playsInline: {
      type: Boolean,
      required: false
    },
    poster: {
      type: String,
      required: false
    },
    preferCmcd: {
      type: String,
      required: false
    },
    preferPlayback: {
      type: String,
      required: false
    },
    preload: {
      type: String,
      required: false,
      default: () => "metadata"
    },
    primaryColor: {
      type: String,
      required: false
    },
    renditionOrder: {
      type: String,
      required: false
    },
    secondaryColor: {
      type: String,
      required: false
    },
    src: {
      type: String,
      required: false
    },
    startTime: {
      type: Number,
      required: false
    },
    storyboardSrc: {
      type: String,
      required: false
    },
    streamType: {
      type: String,
      required: false
    },
    targetLiveWindow: {
      type: Number,
      required: false
    },
    theme: {
      type: String,
      required: false
    },
    themeProps: {
      type: Object,
      required: false
    },
    thumbnailTime: {
      type: Number,
      required: false
    },
    title: {
      type: String,
      required: false
    },
    tokens: {
      type: Object,
      required: false
    },
    volume: {
      type: Number,
      required: false
    }
  },
  emits: [
    "abort",
    "canPlay",
    "canPlayThrough",
    "emptied",
    "loadStart",
    "loadedData",
    "loadedMetadata",
    "progress",
    "durationChange",
    "volumeChange",
    "rateChange",
    "resize",
    "waiting",
    "play",
    "playing",
    "timeUpdate",
    "pause",
    "seeking",
    "seeked",
    "stalled",
    "suspend",
    "ended",
    "error",
    "cuePointChange",
    "cuePointsChange"
  ],
  setup: (_a) => {
    var _b = _a, {
      data = {},
      disableCookies = true,
      disableTracking = true,
      preload = "metadata"
    } = _b, otherProps = __objRest$1(_b, [
      "data",
      "disableCookies",
      "disableTracking",
      "preload"
    ]);
    import('@mux/mux-player');
    const muxPlayerRef = ref();
    const computedProps = __spreadProps$2(__spreadValues$2({}, useVideoPlayer({ data })), {
      disableCookies,
      disableTracking,
      preload
    });
    return {
      muxPlayerRef,
      computedProps,
      otherProps
    };
  },
  mounted() {
    if (this.muxPlayerRef) {
      this.muxPlayerRef.addEventListener("abort", (event) => {
        this.$emit("abort", event);
      });
      this.muxPlayerRef.addEventListener("canplay", (event) => {
        this.$emit("canPlay", event);
      });
      this.muxPlayerRef.addEventListener("canplaythrough", (event) => {
        this.$emit("canPlayThrough", event);
      });
      this.muxPlayerRef.addEventListener("emptied", (event) => {
        this.$emit("emptied", event);
      });
      this.muxPlayerRef.addEventListener("loadstart", (event) => {
        this.$emit("loadStart", event);
      });
      this.muxPlayerRef.addEventListener("loadeddata", (event) => {
        this.$emit("loadedData", event);
      });
      this.muxPlayerRef.addEventListener("loadedmetadata", (event) => {
        this.$emit("loadedMetadata", event);
      });
      this.muxPlayerRef.addEventListener("progress", (event) => {
        this.$emit("progress", event);
      });
      this.muxPlayerRef.addEventListener("durationchange", (event) => {
        this.$emit("durationChange", event);
      });
      this.muxPlayerRef.addEventListener("volumechange", (event) => {
        this.$emit("volumeChange", event);
      });
      this.muxPlayerRef.addEventListener("ratechange", (event) => {
        this.$emit("rateChange", event);
      });
      this.muxPlayerRef.addEventListener("resize", (event) => {
        this.$emit("resize", event);
      });
      this.muxPlayerRef.addEventListener("waiting", (event) => {
        this.$emit("waiting", event);
      });
      this.muxPlayerRef.addEventListener("play", (event) => {
        this.$emit("play", event);
      });
      this.muxPlayerRef.addEventListener("playing", (event) => {
        this.$emit("playing", event);
      });
      this.muxPlayerRef.addEventListener("timeupdate", (event) => {
        this.$emit("timeUpdate", event);
      });
      this.muxPlayerRef.addEventListener("pause", (event) => {
        this.$emit("pause", event);
      });
      this.muxPlayerRef.addEventListener("seeking", (event) => {
        this.$emit("seeking", event);
      });
      this.muxPlayerRef.addEventListener("seeked", (event) => {
        this.$emit("seeked", event);
      });
      this.muxPlayerRef.addEventListener("stalled", (event) => {
        this.$emit("stalled", event);
      });
      this.muxPlayerRef.addEventListener("suspend", (event) => {
        this.$emit("suspend", event);
      });
      this.muxPlayerRef.addEventListener("ended", (event) => {
        this.$emit("ended", event);
      });
      this.muxPlayerRef.addEventListener("error", (event) => {
        this.$emit("error", event);
      });
      this.muxPlayerRef.addEventListener("cuepointchange", (event) => {
        this.$emit("cuePointChange", event);
      });
      this.muxPlayerRef.addEventListener("cuepointschange", (event) => {
        this.$emit("cuePointsChange", event);
      });
    }
  },
  render() {
    return h("mux-player", __spreadValues$2(__spreadValues$2({
      ref: "muxPlayerRef",
      "stream-type": "on-demand"
    }, toHTMLAttrs(this.computedProps)), toHTMLAttrs(this.otherProps)));
  }
});
const DatocmsVideoPlayerPlugin = {
  install: (Vue) => {
    Vue.component("DatocmsVideoPlayer", VideoPlayer);
  }
};

var __defProp$1 = Object.defineProperty;
var __defProps$1 = Object.defineProperties;
var __getOwnPropDescs$1 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$1(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$1 = (a, b) => __defProps$1(a, __getOwnPropDescs$1(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$1.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$1.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
var __async$1 = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
const useQuerySubscription = (_a) => {
  var _b = _a, {
    enabled = true,
    initialData,
    query,
    token
  } = _b, other = __objRest(_b, [
    "enabled",
    "initialData",
    "query",
    "token"
  ]);
  const error = ref(null);
  const data = ref(unref(initialData) || null);
  const status = ref(
    unref(enabled) ? "connecting" : "closed"
  );
  const subscribeToQueryOptions = other;
  watchEffect((onCleanup) => __async$1(void 0, null, function* () {
    if (query && token && unref(enabled)) {
      const unsubscribe = yield subscribeToQuery(__spreadProps$1(__spreadValues$1({}, subscribeToQueryOptions), {
        query,
        token,
        onStatusChange: (connectionStatus) => {
          status.value = connectionStatus;
        },
        onUpdate: ({ response }) => {
          error.value = null;
          data.value = response.data;
        },
        onChannelError: (errorData) => {
          data.value = null;
          error.value = errorData;
        }
      }));
      onCleanup(unsubscribe);
    }
  }));
  return { data, status, error };
};

var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
const highlightPieces = (textWithHighlightMarker) => {
  return textWithHighlightMarker.split(/\[h\](.+?)\[\/h\]/g).map((text, index) => ({
    text,
    isMatch: index % 2 === 1
  }));
};
function useSiteSearch(config) {
  var _a, _b, _c;
  const state = reactive({
    query: ((_a = config.initialState) == null ? void 0 : _a.query) || "",
    page: ((_b = config.initialState) == null ? void 0 : _b.page) || 0,
    locale: (_c = config.initialState) == null ? void 0 : _c.locale
  });
  const error = ref();
  const response = reactive({
    data: [],
    meta: { total_count: 0 }
  });
  error.value = void 0;
  const resultsPerPage = config.resultsPerPage || 8;
  watchEffect((onCleanup) => {
    let isCancelled = false;
    const run = () => __async(this, null, function* () {
      try {
        if (!state.query) {
          response.data = [];
          response.meta = { total_count: 0 };
          return;
        }
        const request = {
          filter: {
            query: state.query,
            locale: state.locale,
            build_trigger_id: config.buildTriggerId
          },
          page: {
            limit: resultsPerPage,
            offset: resultsPerPage * state.page
          }
        };
        if (config.fuzzySearch) {
          request.fuzzy = "true";
        }
        const results = yield config.client.searchResults.rawList(request);
        if (!isCancelled) {
          response.data = results.data;
          response.meta.total_count = results.meta.total_count;
        }
      } catch (e) {
        if (isCancelled) {
          return;
        }
        if (e instanceof Error) {
          error.value = e.message;
        } else {
          error.value = "Unknown error!";
        }
      }
    });
    run();
    onCleanup(() => {
      isCancelled = true;
    });
  });
  const data = computed(() => {
    return state.query && response.data.length > 0 ? {
      pageResults: response.data.map((rawSearchResult) => ({
        id: rawSearchResult.id,
        url: rawSearchResult.attributes.url,
        title: rawSearchResult.attributes.title,
        titleHighlights: rawSearchResult.attributes.highlight.title ? rawSearchResult.attributes.highlight.title.map(highlightPieces) : [],
        bodyExcerpt: rawSearchResult.attributes.body_excerpt,
        bodyHighlights: rawSearchResult.attributes.highlight.body ? rawSearchResult.attributes.highlight.body.map(highlightPieces) : [],
        raw: toRaw(rawSearchResult)
      })),
      totalResults: response.meta.total_count,
      totalPages: Math.ceil(response.meta.total_count / resultsPerPage)
    } : {
      pageResults: [],
      totalResults: 0,
      totalPages: 0
    };
  });
  return {
    state,
    error,
    data
  };
}

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const toHead = (...args) => {
  const tags = [].concat(...args);
  const titleTag = tags && tags.find((t) => t.tag === "title");
  const metaTags = tags ? tags.filter((t) => t.tag === "meta") : [];
  const linkTags = tags ? tags.filter((t) => t.tag === "link") : [];
  return {
    title: titleTag && titleTag.content,
    meta: metaTags.map((tag) => {
      var _a, _b, _c, _d;
      return __spreadProps(__spreadValues({}, tag.attributes), {
        hid: ((_a = tag.attributes) == null ? void 0 : _a.name) || ((_b = tag.attributes) == null ? void 0 : _b.property),
        vmid: ((_c = tag.attributes) == null ? void 0 : _c.name) || ((_d = tag.attributes) == null ? void 0 : _d.property)
      });
    }),
    link: linkTags.map((tag) => {
      var _a, _b, _c, _d, _e, _f;
      return __spreadProps(__spreadValues({}, tag.attributes), {
        hid: ((_a = tag.attributes) == null ? void 0 : _a.name) || `${(_b = tag.attributes) == null ? void 0 : _b.rel}-${(_c = tag.attributes) == null ? void 0 : _c.sizes}`,
        vmid: ((_d = tag.attributes) == null ? void 0 : _d.name) || `${(_e = tag.attributes) == null ? void 0 : _e.rel}-${(_f = tag.attributes) == null ? void 0 : _f.sizes}`
      });
    })
  };
};

export { DatocmsImagePlugin, DatocmsNakedImagePlugin, DatocmsStructuredTextPlugin, DatocmsVideoPlayerPlugin, Image$1 as Image, NakedImage, StructuredText, VideoPlayer, appendKeyToValidElement, defaultAdapter, isKeyOf, isNil, toHead, toNativeAttrName, toNativeAttrValue, useQuerySubscription, useSiteSearch, useVideoPlayer };
